import _ from 'lodash';
import React from 'react';
import { Button } from 'react-bootstrap';

import RadioButtonList from 'components/ModalWindows/Shared/RadioButtonList';
import { ExportType, isVeevaMedcomms, PdfPageOrder, ProjectsConfig } from 'const';
import { intlGet } from 'utils/intlGet';
import { IExportProps, IExportState, LabelTexts } from './models';
import styles from './styles.module.scss';

const TextByExportType: Record<ExportType, LabelTexts> = {
  [ExportType.HTML_PACKAGE]: {
    title: () => intlGet('ExportWindow.Option', 'HtmlPackage'),
    description: () => intlGet('ExportWindow.Description', 'SaveHTMLLocally'),
  },
  [ExportType.IPAD_PACKAGE]: {
    title: () => intlGet('ExportWindow.Option', 'IPadPackage'),
    description: () => intlGet('ExportWindow.Description', 'SaveIPadPackageLocally'),
  },
  [ExportType.PDF]: {
    title: () => intlGet('ExportWindow.Option', 'ProjectPDF'),
    description: () => intlGet('ExportWindow.Description', 'SavePDFLocally'),
  },
  [ExportType.TRANSLATION_PACKAGE]: {
    title: () => intlGet('ExportWindow.Option', 'TranslationPackage'),
    description: () => intlGet('ExportWindow.Description', 'SaveXliffFiles'),
  },
};

export default class ExportWindow extends React.PureComponent<IExportProps, IExportState> {
  readonly state: IExportState = {
    selectedExportType: ProjectsConfig[this.props.projectType].exportType,
    pdfPageOrder: PdfPageOrder.MOBILE_FIRST,
  };

  private readonly onExportTypeChange = (selectedExportType: ExportType): void => {
    this.setState({
      selectedExportType,
    });
  };

  private readonly onPdfPageOrderChange = (pdfPageOrder: PdfPageOrder): void => {
    this.setState({ pdfPageOrder });
  };

  private readonly exportPackage: React.MouseEventHandler = (e) => {
    let debugFlag = false;
    if (e.altKey && e.shiftKey) {
      debugFlag = true;
    }
    const { exportPackage, hideModal, modalWindowId } = this.props;
    const { selectedExportType, pdfPageOrder } = this.state;

    exportPackage(selectedExportType, debugFlag, pdfPageOrder);
    hideModal(modalWindowId);
  };

  private readonly onCancelClick: React.MouseEventHandler = () => {
    const { hideModal, modalWindowId } = this.props;

    hideModal(modalWindowId);
  };

  private readonly getLabel = (exportType: ExportType): JSX.Element => {
    const { title, description } = TextByExportType[exportType];

    return (
      <div className={styles.label}>
        {title()}
        {!!description && <span className={styles.labelDescription}>{`(${description()})`}</span>}
      </div>
    );
  };

  private readonly getPdfPageOrderLabel = (pdfPageOrder: PdfPageOrder): JSX.Element => {
    const labels: Record<PdfPageOrder, string> = {
      [PdfPageOrder.MOBILE_FIRST]: intlGet('PdfPageOrder', 'MobileFirst'),
      [PdfPageOrder.DESKTOP_FIRST]: intlGet('PdfPageOrder', 'DesktopFirst'),
    };

    return <div className={styles.label}>{labels[pdfPageOrder]}</div>;
  };

  render(): React.ReactNode {
    const { selectedExportType, pdfPageOrder } = this.state;
    const { projectType, modalWindowId, hasBothMobileAndDesktopViews } = this.props;
    const showPdfPageOrder = hasBothMobileAndDesktopViews && !isVeevaMedcomms();

    return (
      <div className={styles.Export}>
        <header>{intlGet('ExportWindow', 'Export')}</header>
        <section>
          <RadioButtonList<ExportType>
            id={modalWindowId}
            options={[ProjectsConfig[projectType].exportType, ExportType.PDF/*, ExportType.TRANSLATION_PACKAGE, DCC-3120 */]}
            listLabel={intlGet('ExportWindow', 'SelectOption')}
            onOptionChange={this.onExportTypeChange}
            isDisabled={_.stubFalse}
            activeOption={selectedExportType}
            getOptionLabel={this.getLabel}
          />
        </section>
        {(selectedExportType === ExportType.PDF || selectedExportType === ExportType.HTML_PACKAGE) && showPdfPageOrder && (
          <section>
            <RadioButtonList<PdfPageOrder>
              id={`${modalWindowId}-pdf-order`}
              options={[PdfPageOrder.MOBILE_FIRST, PdfPageOrder.DESKTOP_FIRST]}
              listLabel={intlGet('PdfPageOrder', 'SelectPdfPageOrder')}
              onOptionChange={this.onPdfPageOrderChange}
              isDisabled={_.stubFalse}
              activeOption={pdfPageOrder}
              getOptionLabel={this.getPdfPageOrderLabel}
            />
          </section>
        )}
        <footer>
          <Button onClick={this.onCancelClick} variant="secondary">{intlGet('ExportWindow.Button', 'Cancel')}</Button>
          <Button onClick={this.exportPackage} variant="primary">{intlGet('ExportWindow.Button', 'Export')}</Button>
        </footer>
      </div>
    );
  }
}
